import React, { useState } from 'react';
import { FaLightbulb, FaClock, FaMagic } from 'react-icons/fa';
import { HiSparkles } from 'react-icons/hi';
import ModuleHeader from "../../moduleHeader";

const defaultPackageData = {
  user: {
    first_name: "Kullanıcı",
    last_name: ""
  },
  package: {
    name: "Self Coaching",
    description: "Kendinize koçluk yapın. Zor durumlarda kendinize soracağınız sorularla farklı bir perspektif kazanın.",
    image_url: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800&q=80"
  }
};

const coachingQuestions = [
  {
    id: 1,
    question: "Bunu yaşayan başka biri olsa ona ne derdin?",
    icon: FaLightbulb,
    description: "Kendinize dışarıdan bakın ve başka biri için ne tavsiye verirdiniz?"
  },
  {
    id: 2,
    question: "Bu duygu geçici mi?",
    icon: FaClock,
    description: "Bu duygunun kalıcı olup olmadığını düşünün. Zamanla nasıl değişecek?"
  },
  {
    id: 3,
    question: "5 yıl sonra bunu hatırlar mısın?",
    icon: FaMagic,
    description: "Gelecekte bu durumu nasıl hatırlayacaksınız? Ne kadar önemli olacak?"
  }
];

export default function SelfCoaching({ id, moduleId, isOwner }) {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isCompleted, setIsCompleted] = useState(false);
  const [packageData] = useState(defaultPackageData);
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);

  const currentQuestion = coachingQuestions[currentQuestionIndex];

  const handleAnswerChange = (value) => {
    setAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: value
    }));
  };

  const handleNext = () => {
    if (currentQuestionIndex < coachingQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      setIsCompleted(true);
    }
  };

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleRestart = () => {
    setCurrentQuestionIndex(0);
    setAnswers({});
    setIsCompleted(false);
  };

  return (
    <div className="w-full h-screen min-h-screen flex items-stretch justify-stretch sm:items-center sm:justify-center bg-[#e8e9ed] relative">
      <div className="flex flex-col items-center w-full min-h-full bg-white shadow-xl border border-gray-200 rounded-none sm:max-w-[390px] sm:h-[700px] sm:rounded-3xl relative overflow-y-auto">
        {/* ModuleHeader */}
        <div className="relative z-30 w-full mb-2">
          <ModuleHeader
            userName={packageData.user?.first_name + " " + packageData.user?.last_name}
            subtitle={packageData.package?.name + " Modülüne Hoş Geldiniz"}
            title={packageData.package?.name}
            description={packageData.package?.description}
            backgroundImage={packageData.package?.image_url}
            onExpandedChange={setIsHeaderExpanded}
            showToggle={true}
            autoHeight={true}
          />
        </div>

        <div className="w-full px-6 py-8 pb-24 flex-1">
          {!isCompleted ? (
            <>
              {/* Progress */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-500">
                    Soru {currentQuestionIndex + 1} / {coachingQuestions.length}
                  </span>
                  <span className="text-xs text-gray-500">
                    {Math.round(((currentQuestionIndex + 1) / coachingQuestions.length) * 100)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-[#3DA7B5] to-[#5ABCC8] h-2 rounded-full transition-all duration-500"
                    style={{ width: `${((currentQuestionIndex + 1) / coachingQuestions.length) * 100}%` }}
                  ></div>
                </div>
              </div>

              {/* Question Card */}
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-2xl p-6 mb-6">
                <div className="text-center mb-6">
                  <div className="flex justify-center mb-4">
                    {(() => {
                      const IconComponent = currentQuestion.icon;
                      return <IconComponent className="text-6xl text-indigo-600 animate-bounce" />;
                    })()}
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-3">{currentQuestion.question}</h2>
                  <p className="text-gray-600 text-sm">{currentQuestion.description}</p>
                </div>

                <div>
                  <textarea
                    value={answers[currentQuestion.id] || ''}
                    onChange={(e) => handleAnswerChange(e.target.value)}
                    placeholder="Düşüncelerinizi buraya yazın..."
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3DA7B5] focus:border-transparent resize-none"
                    rows="6"
                  />
                </div>
              </div>

              {/* Navigation */}
              <div className="flex gap-3">
                {currentQuestionIndex > 0 && (
                  <button
                    onClick={handleBack}
                    className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl py-3 font-semibold transition-all duration-200"
                  >
                    ← Geri
                  </button>
                )}
                <button
                  onClick={handleNext}
                  disabled={!answers[currentQuestion.id]?.trim()}
                  className={`flex-1 bg-gradient-to-r from-[#3DA7B5] to-[#5ABCC8] text-white rounded-xl py-3 font-semibold transition-all duration-200 ${
                    !answers[currentQuestion.id]?.trim() 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:from-[#2F8894] hover:to-[#4A9BA6] transform hover:scale-[1.02]'
                  }`}
                >
                  {currentQuestionIndex === coachingQuestions.length - 1 ? 'Tamamla ✓' : 'İleri →'}
                </button>
              </div>
            </>
          ) : (
            <div className="text-center animate-fadeIn">
              <div className="flex justify-center mb-6">
                <HiSparkles className="text-8xl text-[#3DA7B5] animate-bounce" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Harika! Kendinize Koçluk Yaptınız</h2>
              <p className="text-gray-600 mb-8">
                Sorulara verdiğiniz cevaplar size farklı bir perspektif kazandırmış olmalı. Bu cevapları hatırlayın.
              </p>

              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-2xl p-6 mb-6 text-left space-y-6">
                {coachingQuestions.map((question, index) => {
                  const IconComponent = question.icon;
                  return (
                  <div key={question.id} className="border-b border-indigo-100 pb-4 last:border-0 last:pb-0">
                    <div className="flex items-start gap-3 mb-2">
                      <IconComponent className="text-2xl text-indigo-600 mt-1" />
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-800 mb-2">{question.question}</h3>
                        <p className="text-sm text-gray-600 italic bg-white p-3 rounded-lg">
                          {answers[question.id] || 'Cevap verilmedi'}
                        </p>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>

              <button
                onClick={handleRestart}
                className="w-full bg-gradient-to-r from-[#3DA7B5] to-[#5ABCC8] hover:from-[#2F8894] hover:to-[#4A9BA6] text-white rounded-xl py-3 font-semibold transition-all duration-200 transform hover:scale-[1.02]"
              >
                Tekrar Başla
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

